import { Component, DoCheck, inject, Input } from '@angular/core';
import { catchError, first, forkJoin, of } from 'rxjs';
import { CpqConfiguratorComponent } from '../cpq-configurator/cpq-configurator.component';
import { ProductService } from '../../../services/products/product.service';
import { Product } from '../../../models';

export interface CpqPartIncluded {
  productId: string;
  name: string;
  quantity: number;
}

export interface CpqDocumentGroup {
  type: string;
  documents: any[];
}

/**
 * Information about the current configuration: the articles it consists of and the documents of the selected
 * options, of the articles in their bill of material and of the product, one tab per document type. A tab without
 * content is not shown.
 */
@Component({
  selector: 'lib-cpq-info-tabs',
  templateUrl: './cpq-info-tabs.component.html',
  styleUrls: ['./cpq-info-tabs.component.scss'],
  standalone: false
})
export class CpqInfoTabsComponent implements DoCheck {
  @Input()
  configurator: CpqConfiguratorComponent;

  @Input()
  productDocuments: any[] = [];

  protected productService = inject(ProductService);

  parts: CpqPartIncluded[] = [];
  documentGroups: CpqDocumentGroup[] = [];
  activeTab = 'PARTS_INCLUDED';
  private tabChosen = false;

  private selectionKey: string;
  private productNames = new Map<string, string>();
  private materialDocuments = new Map<string, any[]>();

  ngDoCheck(): void {
    const selected = [...(this.configurator?.featureResults?.values() || [])].flat();
    const key = selected.map(option => option.id).sort().join(',') + '|' + (this.productDocuments || []).length;
    if (key === this.selectionKey) {
      return;
    }
    this.selectionKey = key;
    this.parts = this.getParts();
    this.documentGroups = this.getDocumentGroups(selected);
    if (!this.tabChosen || !this.getTabs().includes(this.activeTab)) {
      this.activeTab = this.getTabs()[0];
    }
    this.fetchMissingProductNames();
    this.fetchMissingMaterialDocuments(selected);
  }

  selectTab(tab: string): void {
    this.activeTab = tab;
    this.tabChosen = true;
  }

  getTabs(): string[] {
    return [...(this.parts.length > 0 ? ['PARTS_INCLUDED'] : []), ...this.documentGroups.map(group => group.type)];
  }

  getDocumentGroup(type: string): CpqDocumentGroup | undefined {
    return this.documentGroups.find(group => group.type === type);
  }

  getDocumentName(document: any): string {
    return document?.s3Uri?.split('/')?.reverse()[0] || 'DOCUMENT';
  }

  protected getParts(): CpqPartIncluded[] {
    const quantities = new Map<string, number>();
    for (const selection of this.configurator?.buildSelection() || []) {
      for (const material of selection.option.materials || []) {
        const quantity = (material.quantity || 1) * (selection.qty || 1);
        quantities.set(material.productId, (quantities.get(material.productId) || 0) + quantity);
      }
    }
    return [...quantities.entries()].map(([productId, quantity]) => ({
      productId,
      quantity,
      name: this.productNames.get(productId) || ''
    }));
  }

  protected getDocumentGroups(selected: { documents?: any[]; materials?: { productId: string }[] }[]): CpqDocumentGroup[] {
    const materialDocuments = selected
      .flatMap(option => option.materials || [])
      .flatMap(material => this.materialDocuments.get(material.productId) || []);
    const documents = [
      ...selected.flatMap(option => option.documents || []),
      ...materialDocuments,
      ...(this.productDocuments || [])
    ].filter((document, index, all) => !document?.s3Uri || all.findIndex(other => other?.s3Uri === document.s3Uri) === index);
    const groups = new Map<string, any[]>();
    for (const document of documents) {
      const type = document?.properties?.documentType || document?.extra?.documentType || 'DOCUMENTS';
      groups.set(type, [...(groups.get(type) || []), document]);
    }
    return [...groups.entries()].map(([type, docs]) => ({ type, documents: docs }));
  }

  /**
   * Product lists come without documents, so the articles of the selected options are read one by one, once each.
   */
  protected fetchMissingMaterialDocuments(selected: { materials?: { productId: string }[] }[]): void {
    const missing = [...new Set(selected.flatMap(option => (option.materials || []).map(m => m.productId)))].filter(
      id => !this.materialDocuments.has(id)
    );
    if (missing.length === 0) {
      return;
    }
    missing.forEach(id => this.materialDocuments.set(id, []));
    forkJoin(missing.map(id => this.productService.getProduct(id).pipe(first(), catchError(() => of(null)))))
      .pipe(first())
      .subscribe(products => {
        products
          .filter(product => !!product)
          .forEach(product => this.materialDocuments.set(product.id, product.extra?.documents || []));
        this.selectionKey = undefined;
      });
  }

  protected fetchMissingProductNames(): void {
    const missing = this.parts.map(part => part.productId).filter(id => !this.productNames.has(id));
    if (missing.length === 0) {
      return;
    }
    this.productService
      .getProductsByIds(missing)
      .pipe(first())
      .subscribe((products: Product[]) => {
        products.forEach(product => this.productNames.set(product.id, product.name));
        this.parts = this.parts.map(part => ({ ...part, name: this.productNames.get(part.productId) || part.name }));
      });
  }
}
